// src/components/Navigation.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import caseStudiesData from '../data/caseStudies.json';
import caseStudyDetailsData from '../data/caseStudyDetails.json';
import { tagCategoryNames } from '../utils/tagConfig';

// Animations
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

const fadeInDown = keyframes`
  from {
    opacity: 0;
    transform: translateY(-12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

// Main navigation wrapper
const Header = styled.header`
  position: fixed;
  top: var(--alert-banner-height, 0px);
  left: 0;
  right: 0;
  background: ${({ $scrolled, theme }) => $scrolled
    ? theme.colors.surface
    : theme.colors.background};
  backdrop-filter: ${({ $scrolled }) => $scrolled ? 'blur(10px)' : 'none'};
  border-bottom: 1px solid ${({ $scrolled, theme }) => $scrolled
    ? theme.colors.borderLight
    : 'transparent'};
  transition: all ${({ theme }) => theme.transitions.base};
  z-index: 9999;

  ${({ $scrolled }) => $scrolled && `
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
    border-bottom-color: rgba(226, 232, 228, 0.8);
  `}
`;

const Nav = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.base} 20px;
  max-width: 100%;
  margin: 0 auto;
  position: relative;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.spacing.base} 20px;
    gap: ${({ theme }) => theme.spacing.base};
  }
`;

// Logo
const Logo = styled(NavLink)`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  letter-spacing: ${({ theme }) => theme.letterSpacing.tight};
  position: relative;
  z-index: ${({ theme }) => theme.zIndex.sticky + 1};
  transition: background-position ${({ theme }) => theme.transitions.base};
  font-family: ${({ theme }) => theme.fonts.heading};
  white-space: nowrap;
  flex-shrink: 0;
  opacity: 0;
  animation: ${fadeInDown} 1.6s cubic-bezier(0.16, 1, 0.3, 1) 0.4s forwards;

  background: linear-gradient(
    135deg,
    ${({ theme }) => theme.colors.primary} 0%,
    ${({ theme }) => theme.colors.primary} 50%,
    ${({ theme }) => theme.colors.secondary} 100%
  );
  background-size: 200% 100%;
  background-position: 0% 0%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  &:hover {
    background-position: 100% 0%;
    text-decoration: none;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: ${({ theme }) => theme.fontSizes.lg};
  }
`;

// Right side container for search and nav links
const RightContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.base};
`;

// Search container with slide animation
const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: none;
  }
`;

// Ghost Button - for subtle actions (search toggle, menu toggle)
const GhostButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
    color: ${({ theme }) => theme.colors.text};
  }

  &:focus {
    outline: 3px solid ${({ theme }) => theme.colors.primary}40;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

// Search toggle button (magnifying glass icon)
const SearchToggle = styled(GhostButton)``;

// Expandable search form
const SearchForm = styled.form`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  animation: ${slideIn} 0.2s ease-out;

  ${({ $visible }) => !$visible && css`
    display: none;
  `}
`;

const SearchInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInputIcon = styled.span`
  position: absolute;
  left: ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: ${({ theme }) => theme.colors.textTertiary};

  svg {
    width: 16px;
    height: 16px;
  }
`;

const SearchInput = styled.input`
  width: 200px;
  height: 44px;
  padding: 0 ${({ theme }) => theme.spacing.base} 0 36px;
  border: 2px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  transition: all ${({ theme }) => theme.transitions.fast};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textTertiary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    width: 150px;
  }
`;

// Primary Button - for main actions (search submit, apply)
const PrimaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  background: ${({ theme }) => theme.colors.primary};
  border: none;
  color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  cursor: pointer;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primaryDark};
  }

  &:focus {
    outline: 3px solid ${({ theme }) => theme.colors.primary}40;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// Secondary Button - for secondary actions (outlined style)
const SecondaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  background: transparent;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  cursor: pointer;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.primary}15;
  }

  &:focus {
    outline: 3px solid ${({ theme }) => theme.colors.primary}40;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const FilterButton = styled(SecondaryButton)`
  width: 44px;
  height: 44px;
  padding: 0;
  position: relative;
  border-color: ${({ $active, theme }) => $active ? theme.colors.primary : theme.colors.borderLight};
  background: ${({ $active, theme }) => $active ? theme.colors.primary : 'transparent'};
  color: ${({ $active, theme }) => $active ? theme.colors.background : theme.colors.textSecondary};
  box-sizing: border-box;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
    background: ${({ $active, theme }) => $active ? theme.colors.primaryDark : theme.colors.primary}15;
  }
`;

const FilterCount = styled.span`
  position: absolute;
  top: -6px;
  right: -6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 4px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.background};
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

// Navigation links container
const NavLinks = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.xl};
  align-items: center;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.spacing.base};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: ${({ theme }) => theme.colors.surface}F5;
    backdrop-filter: blur(10px);
    border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
    padding: ${({ theme }) => theme.spacing.base};
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.sm};
    transform: ${({ $isOpen }) => $isOpen ? 'translateY(0)' : 'translateY(-100%)'};
    opacity: ${({ $isOpen }) => $isOpen ? 1 : 0};
    pointer-events: ${({ $isOpen }) => $isOpen ? 'auto' : 'none'};
    transition: all ${({ theme }) => theme.transitions.base};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    z-index: 10;
  }
`;

// Social icon links
const SocialIconLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: color ${({ theme }) => theme.transitions.fast};
  opacity: 0;
  animation: ${fadeInDown} 1.4s cubic-bezier(0.16, 1, 0.3, 1) forwards;
  animation-delay: calc(1s + var(--nav-index, 0) * 0.24s);

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary}40;
    outline-offset: 2px;
  }

  svg {
    width: 20px;
    height: 20px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    opacity: 1;
    animation: none;
  }
`;

// Mobile menu toggle
const MenuToggle = styled(GhostButton)`
  display: none;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: flex;
  }
`;

// Mobile search button
const MobileSearchButton = styled(GhostButton)`
  display: none;
  color: ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs};

  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    display: flex;
  }
`;

// Filter Modal Overlay (Lightbox)
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 10000;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 80px ${({ theme }) => theme.spacing.lg} ${({ theme }) => theme.spacing.lg};
  overflow-y: auto;
  animation: ${fadeIn} 0.2s ease-out;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 60px ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.base};
  }
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.4);
  width: 100%;
  max-width: 1000px;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  position: relative;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xl};
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary}30;
  position: sticky;
  top: 0;
  background: ${({ theme }) => theme.colors.surface};
  z-index: 1;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const CloseButton = styled(GhostButton)`
  width: 44px;
  height: 44px;
  padding: 0;
`;

const ModalBody = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
`;

const ModalSearchContainer = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  padding-bottom: ${({ theme }) => theme.spacing.xl};
  border-bottom: 2px solid ${({ theme }) => theme.colors.primary}20;
`;

const ModalSearchLabel = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ModalSearchForm = styled.form`
  display: flex;
  width: 100%;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ModalSearchInputWrapper = styled.div`
  position: relative;
  flex: 1;

  svg.search-icon {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    width: 20px;
    height: 20px;
    color: ${({ theme }) => theme.colors.textTertiary};
    pointer-events: none;
  }
`;

const ModalSearchInput = styled.input`
  width: 100%;
  height: 48px;
  padding: 0 ${({ theme }) => theme.spacing.base} 0 48px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSizes.base};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.textTertiary};
  }
`;

const ModalSearchSubmit = styled(PrimaryButton)`
  height: 48px;
  padding: 0 ${({ theme }) => theme.spacing.xl};
  font-size: ${({ theme }) => theme.fontSizes.base};
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: none;
  }
`;

const FilterSectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;

const FilterSectionLabel = styled.h3`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const FilterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const FilterSection = styled.div`
  padding: ${({ theme }) => theme.spacing.base};
  border: 2px solid ${({ theme }) => theme.colors.primary}25;
  border-radius: ${({ theme }) => theme.borderRadius.base};
  background: ${({ theme }) => theme.colors.background};
`;

const FilterSectionToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0;
  margin: 0;
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-align: left;

  svg {
    width: 16px;
    height: 16px;
    transition: transform ${({ theme }) => theme.transitions.fast};
    transform: ${({ $expanded }) => $expanded ? 'rotate(180deg)' : 'rotate(0)'};
  }

  &:hover {
    opacity: 0.8;
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.primary}40;
    outline-offset: 2px;
  }
`;

const FilterSectionContent = styled.div`
  display: ${({ $expanded }) => $expanded ? 'flex' : 'none'};
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding-top: ${({ theme }) => theme.spacing.sm};
  border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const FilterOption = styled.label`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  padding: ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundSecondary};
  }

  input[type="checkbox"] {
    cursor: pointer;
    width: 18px;
    height: 18px;
    accent-color: ${({ theme }) => theme.colors.primary};
  }
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.xl};
  border-top: 2px solid ${({ theme }) => theme.colors.primary}30;
  background: ${({ theme }) => theme.colors.surface};
  gap: ${({ theme }) => theme.spacing.base};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    gap: ${({ theme }) => theme.spacing.xl};
  }
`;

// Danger Button - for destructive actions (clear, delete)
const DangerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.sm};
  background: transparent;
  border: 2px solid ${({ theme }) => theme.colors.error};
  color: ${({ theme }) => theme.colors.error};
  border-radius: ${({ theme }) => theme.borderRadius.base};
  cursor: pointer;
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.error};
    color: ${({ theme }) => theme.colors.background};
  }

  &:focus {
    outline: 3px solid ${({ theme }) => theme.colors.error}40;
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ClearButton = styled(DangerButton)`
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing.xl};
  font-size: ${({ theme }) => theme.fontSizes.base};
`;

const ApplyButton = styled(PrimaryButton)`
  padding: ${({ theme }) => theme.spacing.base} ${({ theme }) => theme.spacing['2xl']};
  font-size: ${({ theme }) => theme.fontSizes.base};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [expandedSections, setExpandedSections] = useState({
    aiTools: false,
    companies: false,
    designTools: false,
    devTools: false,
    roles: false,
    skills: false
  });
  const [selectedFilters, setSelectedFilters] = useState({
    companies: [],
    aiTools: [],
    designTools: [],
    devTools: [],
    roles: [],
    skills: []
  });

  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const modalSearchInputRef = useRef(null);

  // Initialize search from URL params
  useEffect(() => {
    const q = searchParams.get('q');
    if (q) setSearchInput(q);
  }, [searchParams]);

  // Handle openSearch param to open modal from external trigger
  useEffect(() => {
    if (searchParams.get('openSearch') === 'true') {
      setFilterModalOpen(true);
      // Remove the param from URL without navigation
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('openSearch');
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Focus and select search input when modal opens
  useEffect(() => {
    if (filterModalOpen && modalSearchInputRef.current) {
      // Small delay to ensure modal is rendered
      setTimeout(() => {
        modalSearchInputRef.current?.focus();
        modalSearchInputRef.current?.select();
      }, 100);
    }
  }, [filterModalOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on route change
  useEffect(() => {
    setMobileMenuOpen(false);
    setFilterModalOpen(false);
    setSearchExpanded(false);
  }, [location]);

  // Close modals on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        setMobileMenuOpen(false);
        setFilterModalOpen(false);
        setSearchExpanded(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (filterModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [filterModalOpen]);

  // Get filter options from case studies data
  const getFilterOptions = useCallback(() => {
    const allProjects = caseStudiesData.caseStudies.map(study => {
      const details = caseStudyDetailsData.caseStudyDetails.find(d => d.id === study.id);
      return { ...study, tags: details?.tags || {} };
    });

    const options = {
      companies: new Set(),
      aiTools: new Set(),
      designTools: new Set(),
      devTools: new Set(),
      roles: new Set(),
      skills: new Set()
    };

    allProjects.forEach(study => {
      if (study.company) options.companies.add(study.company);
      if (study.tags?.aiTools) study.tags.aiTools.forEach(t => options.aiTools.add(t));
      if (study.tags?.designTools) study.tags.designTools.forEach(t => options.designTools.add(t));
      if (study.tags?.devTools) study.tags.devTools.forEach(t => options.devTools.add(t));
      if (study.tags?.roles) study.tags.roles.forEach(t => options.roles.add(t));
      if (study.tags?.skills) study.tags.skills.forEach(t => options.skills.add(t));
    });

    return {
      companies: Array.from(options.companies).sort(),
      aiTools: Array.from(options.aiTools).sort(),
      designTools: Array.from(options.designTools).sort(),
      devTools: Array.from(options.devTools).sort(),
      roles: Array.from(options.roles).sort(),
      skills: Array.from(options.skills).sort()
    };
  }, []);

  const filterOptions = getFilterOptions();

  const activeFilterCount = Object.values(selectedFilters).reduce(
    (sum, arr) => sum + arr.length, 0
  );

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (searchInput.trim()) params.set('q', searchInput.trim());
    navigate(`/projects${params.toString() ? `?${params.toString()}` : ''}`);
    setSearchExpanded(false);
  };

  const handleFilterToggle = (category, value) => {
    setSelectedFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value)
        ? prev[category].filter(v => v !== value)
        : [...prev[category], value]
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const clearFilters = () => {
    setSelectedFilters({
      companies: [],
      aiTools: [],
      designTools: [],
      devTools: [],
      roles: [],
      skills: []
    });
    setSearchInput('');
  };

  const applyFilters = () => {
    const params = new URLSearchParams();
    if (searchInput.trim()) params.set('q', searchInput.trim());

    Object.entries(selectedFilters).forEach(([key, values]) => {
      if (values.length > 0) {
        params.set(key, values.join(','));
      }
    });

    navigate(`/projects${params.toString() ? `?${params.toString()}` : ''}`);
    setFilterModalOpen(false);
  };

  return (
    <>
      <Header $scrolled={scrolled}>
        <Nav>
          <Logo to="/">Dan Schmitz</Logo>

          <RightContainer>
            <SearchContainer>
              {!searchExpanded ? (
                <SearchToggle onClick={() => setSearchExpanded(true)} aria-label="Search">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </SearchToggle>
              ) : (
                <SearchForm onSubmit={handleSearch} $visible={searchExpanded}>
                  <SearchInputWrapper>
                    <SearchInputIcon>
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </SearchInputIcon>
                    <SearchInput
                      type="text"
                      placeholder="Search projects..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                      autoFocus
                    />
                  </SearchInputWrapper>
                  <FilterButton
                    $active={activeFilterCount > 0}
                    onClick={() => setFilterModalOpen(true)}
                    type="button"
                    aria-label="Filters"
                  >
                    <svg fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
                    </svg>
                    {activeFilterCount > 0 && (
                      <FilterCount>{activeFilterCount}</FilterCount>
                    )}
                  </FilterButton>
                </SearchForm>
              )}
            </SearchContainer>

            <MobileSearchButton onClick={() => setFilterModalOpen(true)}>
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </MobileSearchButton>

            <NavLinks $isOpen={mobileMenuOpen}>
              <SocialIconLink href="https://www.linkedin.com/in/schmitzdan" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" style={{ '--nav-index': 1 }}>
                <svg fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </SocialIconLink>
              <SocialIconLink href="https://github.com/ds1" target="_blank" rel="noopener noreferrer" aria-label="GitHub" style={{ '--nav-index': 2 }}>
                <svg fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
                </svg>
              </SocialIconLink>
              <SocialIconLink href="https://x.com/dan_schmitz" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)" style={{ '--nav-index': 3 }}>
                <svg fill="currentColor" viewBox="0 0 24 24">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                </svg>
              </SocialIconLink>
            </NavLinks>

            <MenuToggle
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </MenuToggle>
          </RightContainer>
        </Nav>
      </Header>

      {/* Filter Modal */}
      {filterModalOpen && (
        <ModalOverlay onClick={() => setFilterModalOpen(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Search & Filter Projects</ModalTitle>
              <CloseButton onClick={() => setFilterModalOpen(false)}>
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </CloseButton>
            </ModalHeader>

            <ModalBody>
              <ModalSearchContainer>
                <ModalSearchLabel>Search</ModalSearchLabel>
                <ModalSearchForm onSubmit={(e) => { e.preventDefault(); applyFilters(); }}>
                  <ModalSearchInputWrapper>
                    <svg className="search-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <ModalSearchInput
                      ref={modalSearchInputRef}
                      type="text"
                      placeholder="e.g. Apple, Figma plugin, AI governance..."
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </ModalSearchInputWrapper>
                  <ModalSearchSubmit type="submit" disabled={!searchInput.trim()}>
                    Search
                  </ModalSearchSubmit>
                </ModalSearchForm>
              </ModalSearchContainer>

              <FilterSectionHeader>
                <FilterSectionLabel>Filter by Category</FilterSectionLabel>
              </FilterSectionHeader>

              <FilterGrid>
                {filterOptions.aiTools.length > 0 && (
                  <FilterSection>
                    <FilterSectionToggle
                      onClick={() => toggleSection('aiTools')}
                      $expanded={expandedSections.aiTools}
                    >
                      {tagCategoryNames.aiTools} ({filterOptions.aiTools.length})
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </FilterSectionToggle>
                    <FilterSectionContent $expanded={expandedSections.aiTools}>
                      {filterOptions.aiTools.map(tool => (
                        <FilterOption key={tool}>
                          <input
                            type="checkbox"
                            checked={selectedFilters.aiTools.includes(tool)}
                            onChange={() => handleFilterToggle('aiTools', tool)}
                          />
                          {tool}
                        </FilterOption>
                      ))}
                    </FilterSectionContent>
                  </FilterSection>
                )}

                {filterOptions.companies.length > 0 && (
                  <FilterSection>
                    <FilterSectionToggle
                      onClick={() => toggleSection('companies')}
                      $expanded={expandedSections.companies}
                    >
                      Companies ({filterOptions.companies.length})
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </FilterSectionToggle>
                    <FilterSectionContent $expanded={expandedSections.companies}>
                      {filterOptions.companies.map(company => (
                        <FilterOption key={company}>
                          <input
                            type="checkbox"
                            checked={selectedFilters.companies.includes(company)}
                            onChange={() => handleFilterToggle('companies', company)}
                          />
                          {company}
                        </FilterOption>
                      ))}
                    </FilterSectionContent>
                  </FilterSection>
                )}

                {filterOptions.designTools.length > 0 && (
                  <FilterSection>
                    <FilterSectionToggle
                      onClick={() => toggleSection('designTools')}
                      $expanded={expandedSections.designTools}
                    >
                      {tagCategoryNames.designTools} ({filterOptions.designTools.length})
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </FilterSectionToggle>
                    <FilterSectionContent $expanded={expandedSections.designTools}>
                      {filterOptions.designTools.map(tool => (
                        <FilterOption key={tool}>
                          <input
                            type="checkbox"
                            checked={selectedFilters.designTools.includes(tool)}
                            onChange={() => handleFilterToggle('designTools', tool)}
                          />
                          {tool}
                        </FilterOption>
                      ))}
                    </FilterSectionContent>
                  </FilterSection>
                )}

                {filterOptions.devTools.length > 0 && (
                  <FilterSection>
                    <FilterSectionToggle
                      onClick={() => toggleSection('devTools')}
                      $expanded={expandedSections.devTools}
                    >
                      {tagCategoryNames.devTools} ({filterOptions.devTools.length})
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </FilterSectionToggle>
                    <FilterSectionContent $expanded={expandedSections.devTools}>
                      {filterOptions.devTools.map(tool => (
                        <FilterOption key={tool}>
                          <input
                            type="checkbox"
                            checked={selectedFilters.devTools.includes(tool)}
                            onChange={() => handleFilterToggle('devTools', tool)}
                          />
                          {tool}
                        </FilterOption>
                      ))}
                    </FilterSectionContent>
                  </FilterSection>
                )}

                {filterOptions.roles.length > 0 && (
                  <FilterSection>
                    <FilterSectionToggle
                      onClick={() => toggleSection('roles')}
                      $expanded={expandedSections.roles}
                    >
                      {tagCategoryNames.roles} ({filterOptions.roles.length})
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </FilterSectionToggle>
                    <FilterSectionContent $expanded={expandedSections.roles}>
                      {filterOptions.roles.map(role => (
                        <FilterOption key={role}>
                          <input
                            type="checkbox"
                            checked={selectedFilters.roles.includes(role)}
                            onChange={() => handleFilterToggle('roles', role)}
                          />
                          {role}
                        </FilterOption>
                      ))}
                    </FilterSectionContent>
                  </FilterSection>
                )}

                {filterOptions.skills.length > 0 && (
                  <FilterSection>
                    <FilterSectionToggle
                      onClick={() => toggleSection('skills')}
                      $expanded={expandedSections.skills}
                    >
                      {tagCategoryNames.skills} ({filterOptions.skills.length})
                      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </FilterSectionToggle>
                    <FilterSectionContent $expanded={expandedSections.skills}>
                      {filterOptions.skills.map(skill => (
                        <FilterOption key={skill}>
                          <input
                            type="checkbox"
                            checked={selectedFilters.skills.includes(skill)}
                            onChange={() => handleFilterToggle('skills', skill)}
                          />
                          {skill}
                        </FilterOption>
                      ))}
                    </FilterSectionContent>
                  </FilterSection>
                )}
              </FilterGrid>
            </ModalBody>

            <ModalFooter>
              <ClearButton onClick={clearFilters} disabled={activeFilterCount === 0 && !searchInput}>
                Clear All
              </ClearButton>
              <ApplyButton onClick={applyFilters}>
                Apply Filters
              </ApplyButton>
            </ModalFooter>
          </ModalContent>
        </ModalOverlay>
      )}
    </>
  );
};

export default Navigation;
